// Export an empty object to satisfy webpack's module requirements
export default {};

import {
  DynamicForm,
  FormControl,
  SelectFieldControl,
  TextFieldControl,
} from "./utils/forms";
import { GenericMultiselect } from "./components/multiselect";
import { AttachmentManager } from "./public/attachment-manager";
import { GenericTypeahead } from "./components/typeahead";
import { html } from "./utils/dom-utils";
import { initializeDynamicFieldVisibility } from "./utils/dynamic-field-visibility";
import {
  AsyncValidOptionSelectedValidator,
  MultiselectRequiredValidator,
  Option,
  TextRequiredValidator,
  EmailValidator,
  ValidatorError,
  ValidOptionSelectedValidator,
  ValidatorFn,
} from "./utils/validation";
import { setupAttachmentWarning } from "./utils/attachment-warning";
import { watchProjectVersion } from "./utils/project-version-watcher";
import { OptionFieldManager } from "./utils/option-field-manager";
import { OptionFieldValidator } from "./utils/option-field-validator";

function isAlreadyManaged(inputId: string): boolean {
  const span = document.querySelector(`[data-validation-for="${inputId}"]`);
  return span && ((span as any)._isTypeaheadManaged || (span as any)._manager);
}

class ConfirmEmailValidator {
  constructor(private getPrimaryEmailValue: () => string) {}

  validateConfirmEmail(
    confirmEmailValue: string | null,
  ): ValidatorError | null {
    const confirmEmail = confirmEmailValue ? confirmEmailValue.trim() : "";
    const primaryEmail = this.getPrimaryEmailValue().trim();

    // If primary email is blank, don't show mismatch error
    if (primaryEmail.length === 0) {
      return null;
    }

    // If confirm email is empty but primary has a value, this is handled by required validation
    if (confirmEmail.length === 0) {
      return null;
    }

    // Both fields have values - check if they match
    if (primaryEmail !== confirmEmail) {
      return {
        message: "Email addresses do not match",
      };
    }

    return null;
  }
}

document.addEventListener("DOMContentLoaded", function () {
  const form = new DynamicForm({});
  const typeaheadContainer = new GenericTypeahead(100);
  typeaheadContainer.init();

  // Store attachment managers for form validation access
  (window as any).attachmentManagers = new Map();

  // Initialize attachment inputs
  document
    .querySelectorAll('input[data-attachment-input="true"]')
    .forEach((input) => {
      const attachmentManager = new AttachmentManager(
        input as HTMLInputElement,
        typeaheadContainer,
      );
      (window as any).attachmentManagers.set(input.id, attachmentManager);
    });
  const multiselectContainer = new GenericMultiselect(100);
  multiselectContainer.init();

  const getDOMHelpers = () => ({
    getFieldGroup: (element: Element) =>
      element.closest("[data-option-field-group]") as HTMLElement,
    getVisibleInput: (container: Element) =>
      container.querySelector('input[type="text"]') as HTMLInputElement,
    getVisibleInputFromParent: (element: Element) =>
      element.parentElement?.querySelector(
        'input[type="text"]',
      ) as HTMLInputElement,
    getValidationSpan: (fieldId: string) =>
      document.querySelector(`[data-validation-for="${fieldId}"]`),
    getFieldContainer: (element: Element) =>
      element.closest(".form-group, [data-depends-on]") as HTMLElement,
    isFieldHidden: (container: HTMLElement | null) =>
      container?.style.display === "none",
    getFieldById: (fieldId: string) =>
      document.getElementById(fieldId) as HTMLInputElement,
    // The confirm input that must match the email input with this id.
    // _TextInput.cshtml writes data-confirm-for on the confirm input.
    getConfirmFieldFor: (fieldId: string) =>
      document.querySelector(
        `[data-confirm-for="${fieldId}"]`,
      ) as HTMLInputElement | null,
  });

  const dom = getDOMHelpers();

  const addEventListeners = (
    element: Element,
    events: Array<{
      type: string;
      handler: EventListener;
      options?: AddEventListenerOptions;
    }>,
  ) => {
    events.forEach(({ type, handler, options }) => {
      element.addEventListener(type, handler, options);
    });
  };

  const displayValidationErrors = (span: Element, errors: ValidatorError[]) => {
    span.innerHTML = errors.length === 0 ? "&nbsp;" : "";
    errors.forEach((error) => {
      const errorEl = html`<div>${error.message}</div>`;
      if (errorEl instanceof Array) throw Error("Should only get one el");
      span.appendChild(errorEl);
    });
  };

  const getFieldAttributes = (
    input: Element,
    visibleInput?: Element | null,
  ) => {
    const getAttribute = (attrName: string) =>
      input.getAttribute(attrName) || visibleInput?.getAttribute(attrName);

    const hasAttribute = (attrName: string) =>
      input.hasAttribute(attrName) ||
      (visibleInput?.hasAttribute(attrName) ?? false);

    return {
      required:
        hasAttribute("required") || hasAttribute("data-multiselect-required"),
      fieldName: getAttribute("data-field-name"),
      isValidEmail: getAttribute("data-valid-email") === "True",
      isValidSyncSelect: !!getAttribute("data-valid-sync-select"),
      isValidAsyncSelect: !!getAttribute("data-valid-async-select"),
      // A confirm input names its partner. A primary email input is any input
      // that a confirm input points at. Neither test reads the field name, so
      // a form can carry more than one email pair.
      confirmFor: getAttribute("data-confirm-for"),
      isConfirmEmail: !!getAttribute("data-confirm-for"),
      isPrimaryEmail: !!input.id && !!dom.getConfirmFieldFor(input.id),
    };
  };

  const detectFieldType = (input: Element) => {
    return {
      isSyncMultiselect: input.hasAttribute("data-sync-multiselect"),
      isAsyncMultiselect: input.hasAttribute("data-async-multiselect"),
      isAsyncTypeahead: input.hasAttribute("data-valid-async-select"),
      isSyncTypeahead: input.hasAttribute("data-sync-typeahead"),
    };
  };

  const createAsyncOptionsPromise = (
    inputId: string,
    isMultiselect: boolean = false,
  ): Promise<Option[]> => {
    const container = isMultiselect ? multiselectContainer : typeaheadContainer;
    const dataFetch = container.getDataFetch(inputId);

    if (!dataFetch) {
      return Promise.resolve([]);
    }

    return new Promise<Option[]>((resolve) => {
      dataFetch.registerOnFetch((options: Promise<Option[]>) => {
        options.then((optionList) => resolve(optionList));
      });
    });
  };

  const extractSyncTypeaheadOptions = (input: Element): Option[] => {
    const optionContainer = input.parentElement?.querySelectorAll(
      "div[data-sync-typeahead-options]",
    );

    if (!optionContainer || optionContainer.length !== 1) {
      console.warn(
        `Sync typeahead ${input.id} missing options container - using empty options`,
      );
      return [];
    }

    const options: Option[] = [];
    optionContainer[0]
      .querySelectorAll("div[data-sync-option-value]")
      .forEach((optElement) => {
        options.push({
          value: optElement.getAttribute("data-sync-option-value")!,
          display: optElement.innerHTML.trim(),
        });
      });

    return options;
  };

  const setupDynamicOptionBasedValidation = (
    input: Element,
    fieldGroup: HTMLElement,
  ): void => {
    const inputId = input.id;
    if (isAlreadyManaged(inputId)) return;

    const validationSpan = document.querySelector(
      `[data-validation-for="${inputId}"]`,
    );
    const fieldTypes = detectFieldType(input);
    const required =
      input.hasAttribute("data-multiselect-required") ||
      input.hasAttribute("required");
    const nameDisplay = input.getAttribute("data-field-name");
    const additionalValidators: ValidatorFn<string>[] = [];

    if (
      required &&
      nameDisplay &&
      !fieldTypes.isSyncMultiselect &&
      !fieldTypes.isAsyncMultiselect
    ) {
      additionalValidators.push(TextRequiredValidator(nameDisplay));
    }

    const getOptionsForFieldType = () => {
      if (fieldTypes.isAsyncTypeahead)
        return createAsyncOptionsPromise(inputId, false);
      if (fieldTypes.isAsyncMultiselect)
        return createAsyncOptionsPromise(inputId, true);
      if (fieldTypes.isSyncTypeahead) return extractSyncTypeaheadOptions(input);
      return [];
    };

    const options = getOptionsForFieldType();
    setupOptionBasedValidation(
      fieldGroup,
      options,
      additionalValidators,
      validationSpan,
    );
  };

  const setupOptionBasedValidation = (
    fieldGroup: HTMLElement,
    options: Option[] | Promise<Option[]>,
    additionalValidators: ValidatorFn<string>[] = [],
    validationSpan?: Element | null,
  ): void => {
    const manager = new OptionFieldManager(fieldGroup);
    const hiddenInput = manager.getHiddenInput();
    const visibleInput = manager.getVisibleInput();

    if (!hiddenInput || !visibleInput) {
      console.error(
        "Option field validation setup failed - missing required inputs",
      );
      return;
    }

    const fieldName = hiddenInput.getAttribute("data-field-name") || "Field";
    const isMultiselect =
      fieldGroup.querySelector("[data-sync-multiselect]") ||
      fieldGroup.querySelector("[data-async-multiselect]");

    // Handle both sync and async options
    if (options instanceof Promise) {
      // For async options (like async typeahead), use the resolved options
      options
        .then((resolvedOptions) => {
          const validator = new OptionFieldValidator(
            () => resolvedOptions,
            () => hiddenInput.value,
            !!isMultiselect,
          );
          manager.setupValidation(validator, additionalValidators, fieldName);

          // Store manager reference for potential cleanup
          if (validationSpan) {
            (validationSpan as any)._manager = manager;
          }
        })
        .catch((error) => {
          console.error(
            `Failed to load options for field ${fieldName}:`,
            error,
          );

          // Set up validation with empty options array as fallback
          const validator = new OptionFieldValidator(
            () => [],
            () => hiddenInput.value,
            !!isMultiselect,
          );
          manager.setupValidation(validator, additionalValidators, fieldName);
        });
    } else {
      // For sync options (like sync typeahead, multiselect)
      const validator = new OptionFieldValidator(
        () => options,
        () => hiddenInput.value,
        !!isMultiselect,
      );
      manager.setupValidation(validator, additionalValidators, fieldName);

      // Store manager reference for potential cleanup
      if (validationSpan) {
        (validationSpan as any)._manager = manager;
      }
    }
  };

  const setupFieldValidation = (input: Element, span: Element) => {
    const name = span.getAttribute("data-validation-for")!;
    if (input == null) return;

    // Skip if this field is already managed by typeahead
    if (isAlreadyManaged(name)) {
      return;
    }

    const isOptionBasedField = dom.getFieldGroup(input) !== null;
    const visibleInput = dom.getVisibleInputFromParent(input);
    const fieldAttrs = getFieldAttributes(input, visibleInput);

    let ctrl: FormControl<any, any> | null = null;
    const validators: ValidatorFn<string>[] = [];

    if (fieldAttrs.required && fieldAttrs.fieldName) {
      validators.push(TextRequiredValidator(fieldAttrs.fieldName));
    }

    if (fieldAttrs.isValidEmail) {
      validators.push(EmailValidator);
    }

    if (fieldAttrs.isConfirmEmail && fieldAttrs.confirmFor) {
      const primaryEmailInput = dom.getFieldById(fieldAttrs.confirmFor);

      if (primaryEmailInput) {
        const confirmEmailValidator = new ConfirmEmailValidator(
          () => primaryEmailInput.value || "",
        );

        validators.push((value: string | null) =>
          confirmEmailValidator.validateConfirmEmail(value),
        );
      }
    }

    if (validators.length > 0 && !isOptionBasedField) {
      ctrl = new TextFieldControl(validators, []);
      form.addControl(name, ctrl);
    }

    if (fieldAttrs.isValidSyncSelect) {
      const fieldGroup = dom.getFieldGroup(input);
      if (!fieldGroup) {
        console.warn(
          `Sync typeahead field ${name} missing data-option-field-group container, using fallback validation`,
        );

        const options: Option[] = typeaheadContainer.getFormWithOptions(name);
        const selectValidators: ValidatorFn<string>[] = [];

        if (fieldAttrs.required && fieldAttrs.fieldName) {
          selectValidators.push(TextRequiredValidator(fieldAttrs.fieldName));
        }

        selectValidators.push(ValidOptionSelectedValidator(options));

        ctrl = new SelectFieldControl(selectValidators, []);
        form.addControl(name, ctrl);
      }
    }

    if (input.hasAttribute("data-sync-multiselect") && fieldAttrs.fieldName) {
      const fieldGroup = dom.getFieldGroup(input);
      if (fieldGroup) {
        const fieldContainer = input.closest(".form-group");
        const dependsOn = fieldContainer?.getAttribute("data-depends-on");
        const dependsOnValue = fieldContainer?.getAttribute(
          "data-depends-on-value",
        );
        const requiresValidation =
          fieldContainer?.getAttribute("data-requires-validation") === "true";

        if (dependsOn && dependsOnValue && requiresValidation) {
          const getDependsOnFieldValue = () => {
            const dependsOnField = dom.getFieldById(dependsOn);
            return dependsOnField ? dependsOnField.value : "";
          };

          const dependsOnField = dom.getFieldById(dependsOn);
          if (dependsOnField) {
            dependsOnField.addEventListener("change", () => {
              const nowRequired = getDependsOnFieldValue() === dependsOnValue;
              if (nowRequired) {
                input.setAttribute("data-multiselect-required", "true");
              } else {
                input.removeAttribute("data-multiselect-required");
              }
            });
          }

          const isCurrentlyRequired =
            getDependsOnFieldValue() === dependsOnValue;
          if (isCurrentlyRequired) {
            input.setAttribute("data-multiselect-required", "true");
          }
        } else if (fieldAttrs.required) {
          input.setAttribute("data-multiselect-required", "true");
        }
      } else {
        console.warn(
          `Multiselect field ${name} missing data-option-field-group container - validation will not work properly`,
        );
      }
    }

    if (fieldAttrs.isValidAsyncSelect) {
      const fieldContainer = input.closest("[data-depends-on]");
      const isHiddenDynamicField =
        fieldContainer &&
        fieldContainer.getAttribute("style")?.includes("display: none");

      if (!isHiddenDynamicField) {
        const fieldGroup = dom.getFieldGroup(input);
        if (fieldGroup) {
          const optionsPromise = createAsyncOptionsPromise(name, false);
          const additionalValidators: ValidatorFn<string>[] = [];
          if (fieldAttrs.required && fieldAttrs.fieldName) {
            additionalValidators.push(
              TextRequiredValidator(fieldAttrs.fieldName),
            );
          }
          setupOptionBasedValidation(
            fieldGroup,
            optionsPromise,
            additionalValidators,
          );
        } else {
          const asyncValidator = new AsyncValidOptionSelectedValidator();
          typeaheadContainer
            .getDataFetch(name)
            .registerOnFetch(asyncValidator.onOptionChange);
          ctrl = new SelectFieldControl([], [asyncValidator]);
          form.addControl(name, ctrl);
        }
      }
    }

    if (ctrl != null) {
      span.innerHTML = "&nbsp;";
      const nonNullCtrl: FormControl<string, string> = ctrl;

      const displayErrors = (errors: ValidatorError[]) =>
        displayValidationErrors(span, errors);

      let lastAsyncErrors: ValidatorError[] = [];
      let userHasInteracted = false;
      const isAsyncTypeahead =
        input.getAttribute("data-async-typeahead") === "true";
      const isSyncTypeahead =
        input.getAttribute("data-sync-typeahead") === "true";

      const validateField = () => {
        const errors = nonNullCtrl.getErrors();
        if (userHasInteracted) {
          displayErrors([...errors, ...lastAsyncErrors]);
        }
        const asyncErrors = nonNullCtrl.getAsyncErrors();
        Promise.all(asyncErrors).then((errs) => {
          const currentAsyncErrors = errs.filter(
            (e) => e != null,
          ) as ValidatorError[];
          lastAsyncErrors = currentAsyncErrors;
          if (userHasInteracted) {
            displayErrors([...errors, ...currentAsyncErrors]);
          }
        });
      };

      const setupValidationEventListeners = () => {
        const commonHandler = (getValue: () => string) => () => {
          userHasInteracted = true;
          nonNullCtrl.setRawValue(getValue());
          validateField();
        };

        if (isAsyncTypeahead || isSyncTypeahead) {
          const visibleInput = dom.getVisibleInputFromParent(input);
          if (visibleInput) {
            const inputElement = input as HTMLInputElement;
            addEventListeners(visibleInput, [
              {
                type: "input",
                handler: commonHandler(() => inputElement.value),
              },
              {
                type: "blur",
                handler: commonHandler(() => inputElement.value),
              },
            ]);
            input.addEventListener("change", (ev: Event) => {
              userHasInteracted = true;
              nonNullCtrl.setRawValue((ev.target as HTMLInputElement).value);
              validateField();
            });
          }
        } else {
          addEventListeners(input, [
            {
              type: "input",
              handler: (ev: Event) => {
                userHasInteracted = true;
                nonNullCtrl.setRawValue((ev.target as HTMLInputElement).value);
                validateField();
              },
            },
            {
              type: "blur",
              handler: commonHandler(() => (input as HTMLInputElement).value),
            },
          ]);
        }
      };

      setupValidationEventListeners();
    }

    const isPrimaryEmail = fieldAttrs.isPrimaryEmail;
    if (isPrimaryEmail && ctrl) {
      const confirmEmailInput = dom.getConfirmFieldFor(input.id);
      const confirmEmailId = confirmEmailInput?.id ?? "";
      const confirmEmailSpan = confirmEmailId
        ? dom.getValidationSpan(confirmEmailId)
        : null;

      if (confirmEmailInput && confirmEmailSpan) {
        const confirmEmailCtrl = form.controls[confirmEmailId];

        input.addEventListener("input", () => {
          if (confirmEmailCtrl) {
            confirmEmailCtrl.setRawValue(confirmEmailInput.value);
            const confirmErrors = confirmEmailCtrl.getErrors();

            const confirmUserInteracted = confirmEmailInput.hasAttribute(
              "data-user-interacted",
            );
            if (confirmUserInteracted) {
              displayValidationErrors(confirmEmailSpan, confirmErrors);
            }
          }
        });
      }
    }

    if (fieldAttrs.isConfirmEmail && input) {
      const markUserInteraction = () =>
        input.setAttribute("data-user-interacted", "true");
      addEventListeners(input, [
        { type: "input", handler: markUserInteraction },
        { type: "blur", handler: markUserInteraction },
      ]);
    }
  };

  setTimeout(() => {
    initializeDynamicFieldVisibility();
  }, 0);

  document.addEventListener("dynamicFieldEnabled", (event: Event) => {
    const customEvent = event as CustomEvent;
    const { inputId } = customEvent.detail;
    const input = dom.getFieldById(inputId);
    const span = dom.getValidationSpan(inputId);
    if (input && span) {
      const fieldGroup = dom.getFieldGroup(input);

      if (fieldGroup) {
        const fieldTypes = detectFieldType(input);

        if (
          fieldTypes.isSyncMultiselect ||
          fieldTypes.isAsyncMultiselect ||
          fieldTypes.isAsyncTypeahead ||
          fieldTypes.isSyncTypeahead
        ) {
          setupDynamicOptionBasedValidation(input, fieldGroup);
        }
      } else {
        setupFieldValidation(input, span);
      }
    }
  });

  document.addEventListener("dynamicFieldDisabled", (event: Event) => {
    const customEvent = event as CustomEvent;
    const { inputId } = customEvent.detail;

    const input = dom.getFieldById(inputId);
    if (input) {
      // Clean up typeahead validation flags
      input.removeAttribute("data-typeahead-validated");

      const validationSpan = dom.getValidationSpan(inputId);
      if (validationSpan) {
        (validationSpan as any)._isTypeaheadManaged = false;
        (validationSpan as any)._manager = null;
      }

      // Reset async typeahead data fetch if applicable
      if (input.hasAttribute("data-valid-async-select")) {
        const dataFetch = typeaheadContainer.getDataFetch(inputId);
        if (dataFetch) {
          typeaheadContainer.resetInput(inputId);
        }
      }
    }
  });

  document.querySelectorAll("[data-validation-for]").forEach((span) => {
    const name = span.getAttribute("data-validation-for")!;

    const input = dom.getFieldById(name);
    if (input) {
      // Special handling for program area changes (needs to run even for managed inputs)
      if (name.includes("ProjectProgramAreaId")) {
        const emitProgramAreaChange = (initial: boolean) => () => {
          const programAreaId =
            parseInt((input as HTMLInputElement).value) || null;
          const event = new CustomEvent("programAreaChanged", {
            detail: { programAreaId, initial },
            bubbles: true,
          });
          document.dispatchEvent(event);
        };

        let isInitialLoadComplete = false;

        input.addEventListener("change", (e) => {
          // Only process change events after initial load is complete
          if (isInitialLoadComplete) {
            emitProgramAreaChange(false)();
          }
        });

        if ((input as HTMLInputElement).value) {
          setTimeout(() => {
            emitProgramAreaChange(true)();
            // Mark initial load as complete after the initial event is processed
            setTimeout(() => {
              isInitialLoadComplete = true;
            }, 10);
          }, 0);
        } else {
          // If no initial value, mark as complete immediately
          isInitialLoadComplete = true;
        }
      }
    }

    // Check for typeahead ownership early to avoid unnecessary work
    if (isAlreadyManaged(name)) {
      return;
    }

    if (input) {
      setupFieldValidation(input, span);
    }
  });

  const setupFormValidation = (htmlForm: Element) => {
    const preventSubmitIfInvalid = (e: Event) => {
      const validationResult = validateAllFields(htmlForm);
      if (validationResult.hasErrors) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        return false;
      }
    };

    const submitButtons = htmlForm.querySelectorAll(
      'button[type="submit"], input[type="submit"]',
    );

    addEventListeners(htmlForm, [
      {
        type: "submit",
        handler: preventSubmitIfInvalid,
        options: { capture: true },
      },
    ]);

    submitButtons.forEach((button) => {
      addEventListeners(button, [
        {
          type: "click",
          handler: preventSubmitIfInvalid,
          options: { capture: true },
        },
      ]);
    });
  };

  document.querySelectorAll("form").forEach(setupFormValidation);

  // Extract validation logic into a function
  function validateAllFields(htmlForm: Element): { hasErrors: boolean } {
    let hasErrors = false;

    // Validate attachment types first
    const attachmentInputs = htmlForm.querySelectorAll(
      'input[data-attachment-input="true"]',
    ) as NodeListOf<HTMLInputElement>;
    attachmentInputs.forEach((attachmentInput) => {
      // Find the AttachmentManager instance for this input
      const attachmentManager = (window as any).attachmentManagers?.get(
        attachmentInput.id,
      ) as AttachmentManager;
      if (attachmentManager) {
        const attachmentErrors = attachmentManager.validateAttachmentTypes();
        if (attachmentErrors.length > 0) {
          hasErrors = true;
          const attachmentErrorContainer = document.getElementById(
            `${attachmentInput.id}-errors`,
          );
          if (attachmentErrorContainer) {
            attachmentErrorContainer.remove();
          }

          const attachmentList = document.getElementById(
            `${attachmentInput.id}-attachment-list`,
          );
          if (attachmentList) {
            const errorDiv =
              html`<div id="${attachmentInput.id}-errors" class="attachment-errors alert alert-danger mt-2"></div>` as HTMLElement;
            attachmentErrors.forEach((error) => {
              const errorItem =
                html`<div class="attachment-error"><i class="fas fa-exclamation-triangle me-2"></i>${error}</div>` as HTMLElement;
              errorDiv.appendChild(errorItem);
            });
            attachmentList.parentElement!.insertBefore(
              errorDiv,
              attachmentList.nextSibling,
            );
          }
        } else {
          const existingErrors = document.getElementById(
            `${attachmentInput.id}-errors`,
          );
          if (existingErrors) {
            existingErrors.remove();
          }
        }
      }
    });

    htmlForm.querySelectorAll("[data-validation-for]").forEach((span) => {
      const fieldName = span.getAttribute("data-validation-for")!;
      const input = htmlForm.querySelector(`#${CSS.escape(fieldName)}`);

      if (input) {
        const fieldContainer = dom.getFieldContainer(input);
        if (dom.isFieldHidden(fieldContainer)) {
          span.innerHTML = "&nbsp;";
          return;
        }

        const isOptionBasedField = dom.getFieldGroup(input) !== null;

        if (isOptionBasedField) {
          const visibleInput = dom.getVisibleInputFromParent(input);

          if (visibleInput) {
            if (!visibleInput.checkValidity()) {
              hasErrors = true;

              const validationMessage = visibleInput.validationMessage;
              if (validationMessage) {
                displayValidationErrors(span, [{ message: validationMessage }]);
              }
            } else {
              (span as HTMLElement).innerHTML = "&nbsp;";
            }
          }
        } else {
          // Check if this is a text input or textarea that supports browser validation
          const isTextInput =
            input.tagName.toLowerCase() === "input" &&
            (input as HTMLInputElement).type === "text";
          const isTextarea = input.tagName.toLowerCase() === "textarea";

          if ((isTextInput || isTextarea) && !input.hasAttribute("hidden")) {
            // Use browser validation for text inputs and textareas
            const inputElement = input as
              | HTMLInputElement
              | HTMLTextAreaElement;
            if (!inputElement.checkValidity()) {
              hasErrors = true;

              const validationMessage = inputElement.validationMessage;
              if (validationMessage) {
                displayValidationErrors(span, [{ message: validationMessage }]);
              }
            } else {
              (span as HTMLElement).innerHTML = "&nbsp;";
            }
          } else {
            // Fallback to form control validation for other field types
            const ctrl = form.controls[fieldName];
            if (ctrl) {
              ctrl.setRawValue((input as HTMLInputElement).value);
              const errors = ctrl.getErrors();

              if (errors.length > 0) {
                hasErrors = true;

                displayValidationErrors(span, errors);
              } else {
                span.innerHTML = "&nbsp;";
              }
            }
          }
        }
      }
    });

    if (hasErrors) {
      let firstErrorField: HTMLElement | null = null;

      // Check regular validation spans
      if (!firstErrorField) {
        const validationSpans = Array.from(
          htmlForm.querySelectorAll("[data-validation-for]"),
        );
        for (const span of validationSpans) {
          if (span.innerHTML !== "&nbsp;" && span.textContent?.trim()) {
            const fieldName = span.getAttribute("data-validation-for")!;
            const fieldInput = htmlForm.querySelector(
              `#${CSS.escape(fieldName)}`,
            ) as HTMLElement;

            if (fieldInput) {
              const fieldContainer = dom.getFieldContainer(fieldInput);
              if (!dom.isFieldHidden(fieldContainer)) {
                firstErrorField = fieldInput;
                break;
              }
            }
          }
        }
      }

      if (firstErrorField) {
        const fieldGroup = dom.getFieldGroup(firstErrorField);
        if (fieldGroup) {
          const visibleInput = fieldGroup.querySelector(
            "[data-option-field-visible]",
          ) as HTMLInputElement;
          if (visibleInput) {
            visibleInput.focus();
          } else {
            firstErrorField.focus();
          }
        } else {
          firstErrorField.focus();
        }
      }
    }

    return { hasErrors };
  }

  setupAttachmentWarning();

  const projectVersion = document.querySelector<HTMLElement>(
    "[data-project-version]",
  );
  if (projectVersion) {
    watchProjectVersion(projectVersion);
  }
});
